import { useEffect } from 'react';
import type { Expression, SymbolLayout, SymbolPaint } from 'mapbox-gl';
import { useMap } from 'react-map-gl';
import { drawWellSymbol } from 'components/cross-section/well-symbols';

/**
 * The map symbol the four Create New Well statuses share (Jonathan, INS-13300): one hollow
 * hexagon, the same shape the cross-section and gun barrel draw them as. Registered once per
 * map as an SDF image, so every layer tints it with its own status colour (`icon-color`).
 */
export const PRE_PERMIT_HEXAGON_IMAGE = 'pre-permit-hexagon';

// Drawn at 2x and registered at that pixel ratio, so the edges stay clean on a retina display.
const IMAGE_SIZE = 24;
const PIXEL_RATIO = 2;

// Sized to sit beside the bore-end circles (`circlePaint`, radius 1 → 4 px over zoom 5 → 15).
const ICON_SIZE: Expression = [
  'interpolate',
  ['linear'],
  ['zoom'],
  5,
  0.25,
  10,
  0.5,
  15,
  0.9,
];

/**
 * @return {ImageData | null} The hexagon, white on transparent, or null with no 2d context.
 */
const drawHexagon = (): ImageData | null => {
  const canvas = document.createElement('canvas');
  canvas.width = IMAGE_SIZE;
  canvas.height = IMAGE_SIZE;
  const context = canvas.getContext('2d');
  if (!context) {
    return null;
  }
  // White on transparent: an SDF image takes its colour from the layer.
  drawWellSymbol(context, {
    symbol: 'hollowHexagon',
    color: '#ffffff',
    size: IMAGE_SIZE,
    scale: PIXEL_RATIO,
  });
  return context.getImageData(0, 0, IMAGE_SIZE, IMAGE_SIZE);
};

/**
 * Registers PRE_PERMIT_HEXAGON_IMAGE on the enclosing map, again after a style change (which
 * empties the atlas). Call from a component rendered inside `<Map>`.
 *
 * Registered as soon as the map has a style, not once it is "loaded": `addImage` needs only the
 * style, while `isStyleLoaded()` is false whenever a source is still loading, which is exactly when
 * a layer mounts on an already-loaded map (ticking Wells), long after `style.load` fired.
 * `styleimagemissing` is the backstop for a layer that asks for the image before this runs.
 */
export const usePrePermitHexagonImage = (): void => {
  const { current } = useMap();

  useEffect(() => {
    const map = current?.getMap();
    if (!map) {
      return undefined;
    }
    /**
     * Adds the image unless the map has no style yet or already has it.
     */
    const register = () => {
      // `style` is set by setStyle, before it loads; mapbox-gl's types leave it out.
      if (!('style' in map && map.style)) {
        return;
      }
      if (map.hasImage(PRE_PERMIT_HEXAGON_IMAGE)) {
        return;
      }
      const image = drawHexagon();
      if (image) {
        map.addImage(PRE_PERMIT_HEXAGON_IMAGE, image, {
          sdf: true,
          pixelRatio: PIXEL_RATIO,
        });
      }
    };
    /**
     * Registers the image when a layer asks for it and it is missing.
     *
     * @param {{ id: string }} event - The missing image's id.
     */
    const onImageMissing = (event: { id: string }) => {
      if (event.id === PRE_PERMIT_HEXAGON_IMAGE) {
        register();
      }
    };

    register();
    map.on('style.load', register);
    map.on('styleimagemissing', onImageMissing);
    return () => {
      map.off('style.load', register);
      map.off('styleimagemissing', onImageMissing);
    };
  }, [current]);
};

/** A point layer's layout for the hexagon. */
export const prePermitHexagonLayout: SymbolLayout = {
  'icon-image': PRE_PERMIT_HEXAGON_IMAGE,
  'icon-allow-overlap': true,
  'icon-ignore-placement': true,
  'icon-size': ICON_SIZE,
};

/**
 * @param {string | Expression} color - The status colour, or a `match` over the statuses.
 * @return {SymbolPaint} The hexagon tinted that colour.
 */
export const prePermitHexagonPaint = (
  color: string | Expression
): SymbolPaint => ({
  'icon-color': color,
});
